import { FC, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import SlotCounter from 'react-slot-counter';

import { Box, Button, Fade, Flex, Grid, SlideFade, Text, keyframes } from '@chakra-ui/react';
import routes from 'routes';

import CardPlaceholder from 'components/builder/CardPlaceholder';
import PlayerCard from 'components/builder/player-card/PlayerCard';
import { BuilderProps } from 'components/pages/BuilderPage/types';

import { ReactComponent as BackArrowIcon } from 'icons/back-arr.svg';
import { ReactComponent as InfoIcon } from 'icons/info.svg';
import { ReactComponent as LogoIcon } from 'icons/logo-bg.svg';

interface CustomHTMLDivElement extends HTMLDivElement {
  disintegrated?: boolean;
}

const Builder: FC<BuilderProps> = ({
  cardsWithStatus,
  selectedCards,
  card,
  contest,
  benchCard,
  chooseCard,
  animation,
  participation,
  isAnimating,
  showTutorialToggle,
  showTutorial,
}) => {
  const [restCardsLength, setRestCardsLength] = useState<number>(7);
  const playerCardRef = useRef<CustomHTMLDivElement>(null);
  const cutBlockRef = useRef<CustomHTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showPlayerCardToast, setShowPlayerCardToast] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setRestCardsLength((num) => 7 - cardsWithStatus.length);
  }, [cardsWithStatus]);

  const canCut = restCardsLength > 3 - selectedCards.length + (benchCard ? 0 : 1);
  const isDisabled = !card || isAnimating;

  useEffect(() => {
    if (card) {
      const timer = setTimeout(() => {
        setIsVisible(!!card);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [card]);

  useEffect(() => {
    if (!!showTutorial && !localStorage.getItem(`sbc_player_cardToast_${contest?.league}`)) {
      setTimeout(() => {
        setShowPlayerCardToast(true);
        setTimeout(() => {
          setShowPlayerCardToast(false);
          setTimeout(() => {
            localStorage.setItem(`sbc_player_cardToast_${contest?.league}`, 'true');
          }, 200);
        }, 4000);
      }, 400);
    }
  }, [!!showTutorial]);

  const leagueHeader = () => {
    let title = '';
    let subtitle = '';

    const currentDate = new Date(contest?.startsAt || new Date());
    const month = currentDate.getMonth() + 1;
    const day = currentDate.getDate();
    const dateString = `${month}/${day}`;

    if (contest?.league === 'nba') {
      title = `NBA Lineup ${dateString}`;
      subtitle = 'Score from Total PTS';
    } else if (contest?.league === 'wnba') {
      title = `WNBA Lineup ${dateString}`;
      subtitle = 'Score from Total PTS';
    } else if (contest?.league === 'nfl') {
      title = `NFL Lineup ${dateString}`;
      subtitle = 'Score from Total YDS';
    } else if (contest?.league === 'mlb') {
      title = `MLB Lineup ${dateString}`;
      subtitle = 'Score from Bases and Hits';
    }

    return {
      title: title,
      subtitle: subtitle,
    };
  };

  const backButtonHandler = () => {
    navigate(-1);
  };

  if (contest && contest.status !== 'active') {
    return <Navigate to={routes.home} replace />;
  }

  if (participation && participation?.status !== 'cards_pending') {
    return <Navigate to={routes.squad} replace />;
  }

  if (!participation) {
    return null;
  }

  const getRefPositionRelativeToViewport = (ref: React.RefObject<HTMLElement>) => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      return {
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
        x: rect.x,
        y: rect.y,
      };
    }
    return null;
  };

  const playerCardPosition = getRefPositionRelativeToViewport(playerCardRef);

  return (
    <Fade in style={{ overflow: 'hidden', gridRow: 'span 2' }}>
      <Grid gridTemplateRows="auto 1fr auto auto" h="100%" backgroundColor="#242424">
        <Grid gridTemplateColumns="48px 1fr 48px" gap="8px" p="6.5px 0 7.5px" backgroundColor="#282828" mt="-8px">
          <Box display="flex" alignItems="center" justifyContent="center">
            <Box
              onClick={backButtonHandler}
              height="36px"
              width="36px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="20px"
            >
              <BackArrowIcon />
            </Box>
          </Box>
          <Box textAlign="center">
            <Text fontSize="14px" fontWeight="800" lineHeight="1.2">
              {leagueHeader().title}
            </Text>
            <Text variant="text1" mt="2px" display="flex" alignItems="center" justifyContent="center" gap="4px">
              {leagueHeader().subtitle}
              <Box as="span" fontSize="12px" fontWeight="800" lineHeight="1.2" onClick={showTutorialToggle}>
                <InfoIcon width="12px" height="12px" />
              </Box>
            </Text>
          </Box>
          <Box></Box>
        </Grid>

        <Box pos="relative" overflow="hidden" px="15px" display="flex" justifyContent="center" alignItems="center">
          <Fade
            in={isVisible}
            style={{
              padding: '20px 0',
              position: 'relative',
              width: '100%',
              maxWidth: '375px',
              maxHeight: '562.5px',
              height: '100%',
              justifyContent: 'center',
              margin: 'auto auto',
              display: 'flex',
            }}
          >
            {card && (
              <PlayerCard
                ref={playerCardRef}
                mode="builder"
                detailed
                card={card}
                animation={`${animation} 0.6s forwards`}
                contest={contest}
                changeStateTrigger={animation}
              />
            )}
            <SlideFade
              in={showPlayerCardToast}
              offsetY={20}
              unmountOnExit
              style={{
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                top: '0%',
                left: '0%',
                width: '100%',
                height: '100%',
                zIndex: 100,
                pointerEvents: 'none',
              }}
            >
              <Flex
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                padding="5px 6px"
                lineHeight="1"
                textAlign="center"
                fontSize="16px"
                fontWeight="800"
              >
                <Box backgroundColor="rgba(248, 60, 60, 1)" borderRadius="4px" p="4px 8px" mb="-4px">
                  tap card
                </Box>
                <Box backgroundColor="rgba(248, 60, 60, 1)" borderRadius="4px" p="4px 8px">
                  to see stats 👆
                </Box>
              </Flex>
            </SlideFade>
          </Fade>
        </Box>
        <Flex flexDirection="column" backgroundColor="#242424" px="12px">
          <Grid
            templateColumns="60px 1fr 60px"
            textAlign="center"
            textTransform="uppercase"
            margin="11px -12px 0"
            px="12px"
            zIndex="4"
          >
            <Box>
              <Text mb="12px" variant="text2" color="white">
                CARDS
              </Text>
              <Text fontWeight="500" fontFamily="JetBrains Mono" lineHeight="1.1" fontSize="40px" pt="5px">
                <SlotCounter value={restCardsLength} sequentialAnimationMode useMonospaceWidth />
              </Text>
            </Box>
            <Box>
              <Text mb="12px" variant="text2" color="white">
                STARTERS
              </Text>
              <Flex gap="5px" justifyContent="center">
                <CardPlaceholder
                  w="60px"
                  fit="contain"
                  flex="inherit"
                  showAnimation
                  card={selectedCards[0]}
                  type="start"
                  noLabel={!selectedCards[0]}
                />
                <CardPlaceholder
                  w="60px"
                  fit="contain"
                  flex="inherit"
                  showAnimation
                  card={selectedCards[1]}
                  type="start"
                  noLabel={!selectedCards[1]}
                />
                <CardPlaceholder
                  w="60px"
                  fit="contain"
                  flex="inherit"
                  showAnimation
                  card={selectedCards[2]}
                  type="start"
                  noLabel={!selectedCards[2]}
                />
              </Flex>
            </Box>
            <Box>
              <Text mb="12px" variant="text2" color="white">
                BENCH
              </Text>
              <CardPlaceholder w="60px" showAnimation card={benchCard} type="bench" noLabel={!benchCard} />
            </Box>
          </Grid>
          <Flex justify="center" gap="8px" pt="16px" pb="16px" zIndex="5">
            <Button
              h="52px !important"
              mt="0px !important"
              fontSize="20px"
              lineHeight="32px"
              flex="1"
              _active={{
                transform: 'translateY(2px)',
                paddingTop: '1px',
                background: 'var(--chakra-colors-brand-600)',
              }}
              isDisabled={selectedCards.length >= 3}
              data-cy="start-button"
              onClick={() => {
                if (!isDisabled) {
                  chooseCard(card, 'start');
                  if (playerCardRef.current) {
                    playerCardRef.current.style.opacity = '1';
                  }
                }
              }}
            >
              start
            </Button>
            <Button
              h="52px !important"
              mt="0px !important"
              fontSize="20px"
              lineHeight="32px"
              flex="1"
              _active={{
                transform: 'translateY(2px)',
                paddingTop: '1px',
                background: 'var(--chakra-colors-brand-600)',
              }}
              isDisabled={!!benchCard}
              data-cy="bench-button"
              onClick={() => {
                if (!isDisabled) {
                  chooseCard(card, 'bench');
                  if (playerCardRef.current) {
                    playerCardRef.current.style.opacity = '1';
                  }
                }
              }}
            >
              bench
            </Button>
            <Button
              h="52px !important"
              mt="0px !important"
              fontSize="20px"
              lineHeight="32px"
              flex="1"
              _active={{
                transform: 'translateY(2px)',
                paddingTop: '1px',
                background: 'var(--chakra-colors-brand-600)',
              }}
              isDisabled={!canCut}
              data-cy="cut-button"
              onClick={() => {
                if (!isDisabled) {
                  chooseCard(card, 'cut');
                }
              }}
            >
              cut
            </Button>
          </Flex>
        </Flex>
      </Grid>
    </Fade>
  );
};

export default Builder;
